import {
  AccessDeniedComponent,
  AdminUiModule,
  DashboardComponent,
  ErrorComponent,
  LayoutComponent,
  LoginComponent,
  NotFoundComponent,
  NotificationsComponent,
} from '@acal-frontend/admin-ui';
import { AuthGuard } from '@acal-frontend/ui-guards';
import {
  apiInterceptorProvider,
  errorInterceptorProvider,
  jwtInterceptorProvider,
} from '@acal-frontend/ui-interceptors';
import { reloadRouteOnParamChangeStrategyProvider } from '@acal-frontend/ui-services';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouterModule } from '@angular/router';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular-ivy';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [AppComponent],

  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    HttpClientModule,

    // Routing
    RouterModule.forRoot(
      [
        {
          path: '',
          component: LayoutComponent,
          canActivate: [AuthGuard],
          canLoad: [AuthGuard],

          children: [
            { path: '', redirectTo: 'dashboard', pathMatch: 'full' },

            {
              path: 'dashboard',
              component: DashboardComponent,
              canActivate: [AuthGuard],
            },

            {
              path: 'blacklist-words',
              loadChildren: () =>
                import('./blacklist-word/blacklist-word.module').then(
                  (m) => m.BlacklistWordModule,
                ),
            },

            {
              path: 'chat-messages',
              loadChildren: () =>
                import('./chat-message/chat-message.module').then(
                  (m) => m.ChatMessageModule,
                ),
            },

            {
              path: 'claim-resolution-types',
              loadChildren: () =>
                import(
                  './claim-resolution-type/claim-resolution-type.module'
                ).then((m) => m.ClaimResolutionTypeModule),
            },

            {
              path: 'claims',
              loadChildren: () =>
                import('./claim/claim.module').then((m) => m.ClaimModule),
            },

            {
              path: 'claim-types',
              loadChildren: () =>
                import('./claim-type/claim-type.module').then(
                  (m) => m.ClaimTypeModule,
                ),
            },

            {
              path: 'countries',
              loadChildren: () =>
                import('./country/country.module').then((m) => m.CountryModule),
            },

            {
              path: 'currencies',
              loadChildren: () =>
                import('./currency/currency.module').then(
                  (m) => m.CurrencyModule,
                ),
            },

            {
              path: 'customers',
              loadChildren: () =>
                import('./customer/customer.module').then(
                  (m) => m.CustomerModule,
                ),
            },

            {
              path: 'experts',
              loadChildren: () =>
                import('./expert/expert.module').then((m) => m.ExpertModule),
            },

            {
              path: 'expertises',
              loadChildren: () =>
                import('./expertise/expertise.module').then(
                  (m) => m.ExpertiseModule,
                ),
            },

            {
              path: 'global-params',
              loadChildren: () =>
                import('./global-param/global-param.module').then(
                  (m) => m.GlobalParamModule,
                ),
            },

            {
              path: 'hourly-rate-configs',
              loadChildren: () =>
                import('./hourly-rate-config/hourly-rate-config.module').then(
                  (m) => m.HourlyRateConfigModule,
                ),
            },

            {
              path: 'languages',
              loadChildren: () =>
                import('./language/language.module').then(
                  (m) => m.LanguageModule,
                ),
            },

            {
              path: 'notifications',
              component: NotificationsComponent,
              canActivate: [AuthGuard],
            },

            {
              path: 'payments',
              loadChildren: () =>
                import('./payment/payment.module').then((m) => m.PaymentModule),
            },

            {
              path: 'settlements',
              loadChildren: () =>
                import('./settlement/settlement.module').then(
                  (m) => m.SettlementModule,
                ),
            },

            {
              path: 'transfer-fee-configs',
              loadChildren: () =>
                import('./transfer-fee-config/transfer-fee-config.module').then(
                  (m) => m.TransferFeeConfigModule,
                ),
            },
          ],
        },

        { path: 'login', component: LoginComponent },
        { path: 'error', component: ErrorComponent },
        { path: '404', component: NotFoundComponent },
        { path: '403', component: AccessDeniedComponent },
        { path: '**', redirectTo: '404' },
      ],
      { initialNavigation: 'enabledBlocking' },
    ),

    // 3rd-party Libs
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),

    // ACAL Libs
    AdminUiModule,
  ],

  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {
        /**/
      },
      deps: [Sentry.TraceService],
      multi: true,
    },

    { provide: 'API_BASE_URL', useValue: environment.apiBaseUrl },
    { provide: 'API_PAGE_SIZE', useValue: environment.apiPageSize },
    { provide: 'APP_SCOPE', useValue: environment.scope },
    { provide: 'APP_VERSION', useValue: environment.appVersion },
    { provide: 'AUTH_API', useValue: environment.authApi },
    {
      provide: 'CREDENTIALS_KEY',
      useValue: environment.credentials?.key,
    },
    { provide: 'IMAGE_BASE_URL', useValue: environment.imageBaseUrl },
    { provide: 'JITSI_BASE_URL', useValue: environment.jitsiBaseUrl },
    {
      provide: 'JWT_REFRESH_TOKEN_NONCE_NAME',
      useValue: environment.jwt.refreshTokenNonce,
    },
    { provide: 'OZD__SELF_SERVICE_PROFILES', useValue: null },
    { provide: 'PUBLIC_URL', useValue: environment.publicUrl },
    { provide: 'SOCKET_IO_URL', useValue: environment.socketIoUrl },
    {
      provide: 'VOICE_RECORDING_MAX_DURATION_IN_MS',
      useValue: environment.voiceRecording?.maxDurationInMs,
    },
    {
      provide: 'VOICE_RECORDING_TICK_INTERVAL_IN_MS',
      useValue: environment.voiceRecording?.tickIntervalInMs,
    },

    jwtInterceptorProvider,
    apiInterceptorProvider,
    errorInterceptorProvider,

    reloadRouteOnParamChangeStrategyProvider,
  ],

  bootstrap: [AppComponent],
})
export class AppModule {}
